export type TemplateCategory =
  | "onboarding"
  | "transactional"
  | "marketing"
  | "alerts"
  | "follow-up"
  | "sales"
  | "retention"
  | "invoice"
  | "discount"
  | "newsletter"
  | "confirmation"
  | "security"
  | "social"
  | "product-update"
  | "survey"
  | "collaboration";

export interface TemplateLibraryEntry {
  id: string;
  slug: string;
  brand: string;
  title: string;
  description: string;
  category: TemplateCategory;
  tags: string[];
  previewClass: string;
  createdAt: string;
}

export const templateLibrary: TemplateLibraryEntry[] = [
  {
    id: "acme-welcome",
    slug: "welcome",
    brand: "acme",
    title: "Welcome Email",
    description: "A warm onboarding email for new users with quick-start steps.",
    category: "onboarding",
    tags: ["welcome", "onboarding", "getting-started"],
    previewClass: "bg-indigo-100",
    createdAt: "2025-11-01",
  },
  {
    id: "acme-receipt",
    slug: "receipt",
    brand: "acme",
    title: "Payment Receipt",
    description: "A clean payment confirmation with itemized receipt.",
    category: "invoice",
    tags: ["receipt", "invoice", "payment"],
    previewClass: "bg-emerald-100",
    createdAt: "2025-11-01",
  },
  {
    id: "acme-password-reset",
    slug: "password-reset",
    brand: "acme",
    title: "Password Reset",
    description: "A secure password reset email with an expiring action link.",
    category: "transactional",
    tags: ["password", "reset", "security"],
    previewClass: "bg-amber-100",
    createdAt: "2025-11-05",
  },
  {
    id: "stripe-payment-receipt",
    slug: "payment-receipt",
    brand: "stripe",
    title: "Payment Receipt",
    description: "A polished Stripe-style invoice receipt with merchant, payment, and line-item detail.",
    category: "invoice",
    tags: ["stripe", "receipt", "invoice", "billing"],
    previewClass: "bg-violet-100",
    createdAt: "2025-11-10",
  },
  {
    id: "stripe-subscription-confirmation",
    slug: "subscription-confirmation",
    brand: "stripe",
    title: "Subscription Confirmation",
    description: "A subscription activation email with plan limits, renewal date, and billing portal CTA.",
    category: "confirmation",
    tags: ["stripe", "subscription", "saas", "billing"],
    previewClass: "bg-violet-100",
    createdAt: "2025-11-10",
  },
  {
    id: "stripe-payment-failed",
    slug: "payment-failed",
    brand: "stripe",
    title: "Payment Failed",
    description: "A high-trust failed payment notice with retry timing and update-card guidance.",
    category: "invoice",
    tags: ["stripe", "failed-payment", "dunning", "billing"],
    previewClass: "bg-orange-100",
    createdAt: "2025-11-12",
  },
  {
    id: "airbnb-booking-confirmation",
    slug: "booking-confirmation",
    brand: "airbnb",
    title: "Booking Confirmation",
    description: "A travel booking confirmation with reservation details, host contact, and trip CTA.",
    category: "confirmation",
    tags: ["airbnb", "travel", "booking", "reservation"],
    previewClass: "bg-rose-100",
    createdAt: "2025-11-15",
  },
  {
    id: "airbnb-trip-reminder",
    slug: "trip-reminder",
    brand: "airbnb",
    title: "Trip Reminder",
    description: "An arrival reminder with check-in essentials, host messaging, and travel tips.",
    category: "retention",
    tags: ["airbnb", "trip", "travel", "check-in"],
    previewClass: "bg-pink-100",
    createdAt: "2025-11-15",
  },
  {
    id: "amazon-order-confirmation",
    slug: "order-confirmation",
    brand: "amazon",
    title: "Order Confirmation",
    description: "A marketplace-style confirmation with order number, items, shipping, and total.",
    category: "confirmation",
    tags: ["amazon", "ecommerce", "order", "receipt"],
    previewClass: "bg-amber-100",
    createdAt: "2025-11-18",
  },
  {
    id: "amazon-shipping-update",
    slug: "shipping-update",
    brand: "amazon",
    title: "Shipping Update",
    description: "A delivery progress email with live ETA, courier detail, and package options.",
    category: "transactional",
    tags: ["amazon", "shipping", "delivery", "tracking"],
    previewClass: "bg-yellow-100",
    createdAt: "2025-11-18",
  },
  {
    id: "shopify-order-confirmation",
    slug: "order-confirmation",
    brand: "shopify",
    title: "Order Confirmation",
    description: "A Shopify-style purchase confirmation with item cards, fulfillment detail, and order management CTA.",
    category: "confirmation",
    tags: ["shopify", "order", "ecommerce", "receipt"],
    previewClass: "bg-emerald-100",
    createdAt: "2025-11-20",
  },
  {
    id: "shopify-abandoned-cart",
    slug: "abandoned-cart",
    brand: "shopify",
    title: "Abandoned Cart",
    description: "A Shopify abandoned cart recovery email with saved items, total, and a return-to-checkout CTA.",
    category: "marketing",
    tags: ["shopify", "cart", "ecommerce", "recovery"],
    previewClass: "bg-green-100",
    createdAt: "2026-03-28",
  },
  {
    id: "uber-ride-receipt",
    slug: "ride-receipt",
    brand: "uber",
    title: "Ride Receipt",
    description: "A sleek trip receipt with route timing, fare breakdown, and rider support follow-up.",
    category: "invoice",
    tags: ["uber", "receipt", "travel", "transport"],
    previewClass: "bg-zinc-200",
    createdAt: "2025-11-22",
  },
  {
    id: "uber-delivery-receipt",
    slug: "delivery-receipt",
    brand: "uber",
    title: "Delivery Receipt",
    description: "An Uber Eats delivery confirmation with order items, delivery fee breakdown, and tip details.",
    category: "invoice",
    tags: ["uber", "delivery", "eats", "receipt"],
    previewClass: "bg-stone-200",
    createdAt: "2026-03-28",
  },
  {
    id: "dropbox-sharing-notification",
    slug: "sharing-notification",
    brand: "dropbox",
    title: "Sharing Notification",
    description: "A file-sharing email with folder access, permission detail, and workspace context.",
    category: "alerts",
    tags: ["dropbox", "sharing", "files", "collaboration"],
    previewClass: "bg-blue-100",
    createdAt: "2025-11-25",
  },
  {
    id: "dropbox-storage-alert",
    slug: "storage-alert",
    brand: "dropbox",
    title: "Storage Alert",
    description: "A Dropbox storage usage warning with quota details, top folders, and upgrade options.",
    category: "alerts",
    tags: ["dropbox", "storage", "quota", "alert"],
    previewClass: "bg-sky-100",
    createdAt: "2026-03-28",
  },
  {
    id: "figma-team-invite",
    slug: "team-invite",
    brand: "figma",
    title: "Team Invite",
    description: "A collaborative invite email for joining a Figma team with project access and starter context.",
    category: "onboarding",
    tags: ["figma", "invite", "design", "team"],
    previewClass: "bg-violet-100",
    createdAt: "2025-11-28",
  },
  {
    id: "figma-comment-notification",
    slug: "comment-notification",
    brand: "figma",
    title: "Comment Notification",
    description: "A Figma design comment notification with file context, commenter details, and a reply CTA.",
    category: "alerts",
    tags: ["figma", "comment", "design", "feedback"],
    previewClass: "bg-purple-100",
    createdAt: "2026-03-28",
  },
  {
    id: "discord-verification",
    slug: "verification",
    brand: "discord",
    title: "Verification Email",
    description: "A Discord-style account verification email with a six-digit code and security details.",
    category: "security",
    tags: ["discord", "verification", "security", "account"],
    previewClass: "bg-indigo-100",
    createdAt: "2025-12-01",
  },
  {
    id: "discord-server-invite",
    slug: "server-invite",
    brand: "discord",
    title: "Server Invite",
    description: "A Discord server invitation with server details, member count, and a join CTA.",
    category: "onboarding",
    tags: ["discord", "server", "invite", "community"],
    previewClass: "bg-violet-100",
    createdAt: "2026-03-28",
  },
  {
    id: "twitter-notification-digest",
    slug: "notification-digest",
    brand: "twitter",
    title: "Notification Digest",
    description: "A social activity roundup with mentions, follows, and trending network updates.",
    category: "alerts",
    tags: ["twitter", "digest", "social", "notifications"],
    previewClass: "bg-sky-100",
    createdAt: "2025-12-03",
  },
  {
    id: "twitter-login-alert",
    slug: "login-alert",
    brand: "twitter",
    title: "Login Alert",
    description: "A Twitter / X new login alert with device details, location, and account security actions.",
    category: "alerts",
    tags: ["twitter", "login", "security", "alert"],
    previewClass: "bg-blue-100",
    createdAt: "2026-03-28",
  },
  {
    id: "google-security-alert",
    slug: "security-alert",
    brand: "google",
    title: "Security Alert",
    description: "A high-trust Google account alert for suspicious sign-ins and account review actions.",
    category: "security",
    tags: ["google", "security", "signin", "account"],
    previewClass: "bg-blue-100",
    createdAt: "2025-12-05",
  },
  {
    id: "google-storage-warning",
    slug: "storage-warning",
    brand: "google",
    title: "Storage Warning",
    description: "A Google One storage quota warning with usage breakdown across Drive, Gmail, and Photos, and upgrade options.",
    category: "transactional",
    tags: ["google", "storage", "quota", "drive"],
    previewClass: "bg-amber-100",
    createdAt: "2026-03-28",
  },
  {
    id: "apple-purchase-receipt",
    slug: "purchase-receipt",
    brand: "apple",
    title: "Purchase Receipt",
    description: "A clean Apple order receipt with hardware line items, coverage, and billing details.",
    category: "invoice",
    tags: ["apple", "receipt", "purchase", "hardware"],
    previewClass: "bg-slate-100",
    createdAt: "2025-12-08",
  },
  {
    id: "apple-icloud-storage",
    slug: "icloud-storage",
    brand: "apple",
    title: "iCloud Storage",
    description: "An Apple iCloud storage upgrade confirmation with plan details, family sharing status, and billing info.",
    category: "transactional",
    tags: ["apple", "icloud", "storage", "subscription"],
    previewClass: "bg-sky-100",
    createdAt: "2026-03-28",
  },
  {
    id: "duolingo-streak-reminder",
    slug: "streak-reminder",
    brand: "duolingo",
    title: "Streak Reminder",
    description: "A gamified practice reminder with streak progress, league stats, and a quick lesson CTA.",
    category: "retention",
    tags: ["duolingo", "streak", "education", "engagement"],
    previewClass: "bg-lime-100",
    createdAt: "2025-12-10",
  },
  {
    id: "duolingo-weekly-progress",
    slug: "weekly-progress",
    brand: "duolingo",
    title: "Weekly Progress",
    description: "A Duolingo weekly learning report with XP earned, lessons completed, and league ranking.",
    category: "marketing",
    tags: ["duolingo", "progress", "education", "weekly"],
    previewClass: "bg-green-100",
    createdAt: "2026-03-28",
  },
  {
    id: "vercel-deployment-success",
    slug: "deployment-success",
    brand: "vercel",
    title: "Deployment Notification",
    description: "A Vercel production deployment email with build metadata and rollout status.",
    category: "confirmation",
    tags: ["vercel", "deployment", "devops", "build"],
    previewClass: "bg-stone-200",
    createdAt: "2025-12-12",
  },
  {
    id: "vercel-domain-verified",
    slug: "domain-verified",
    brand: "vercel",
    title: "Domain Verified",
    description: "A custom domain connection email with SSL, DNS, and project settings follow-up.",
    category: "confirmation",
    tags: ["vercel", "domain", "dns", "ssl"],
    previewClass: "bg-zinc-200",
    createdAt: "2025-12-12",
  },
  {
    id: "github-security-alert",
    slug: "security-alert",
    brand: "github",
    title: "Security Alert",
    description: "A GitHub-style new sign-in alert with IP, browser, and response recommendations.",
    category: "security",
    tags: ["github", "security", "account", "signin"],
    previewClass: "bg-slate-200",
    createdAt: "2025-12-15",
  },
  {
    id: "github-pr-merged",
    slug: "pr-merged",
    brand: "github",
    title: "PR Merged",
    description: "A pull request merge notification with checks, branch, and deployment follow-through.",
    category: "transactional",
    tags: ["github", "pull-request", "engineering", "merge"],
    previewClass: "bg-slate-100",
    createdAt: "2025-12-15",
  },
  {
    id: "slack-welcome",
    slug: "welcome",
    brand: "slack",
    title: "Workspace Welcome",
    description: "A workspace onboarding email with starter channels, app download links, and setup tips.",
    category: "onboarding",
    tags: ["slack", "welcome", "workspace", "team"],
    previewClass: "bg-fuchsia-100",
    createdAt: "2025-12-18",
  },
  {
    id: "slack-channel-digest",
    slug: "channel-digest",
    brand: "slack",
    title: "Channel Digest",
    description: "A Slack weekly channel digest with top threads, mentions, and unread highlights across your workspace.",
    category: "newsletter",
    tags: ["slack", "digest", "channels", "weekly"],
    previewClass: "bg-purple-100",
    createdAt: "2026-03-28",
  },
  {
    id: "notion-getting-started",
    slug: "getting-started",
    brand: "notion",
    title: "Getting Started",
    description: "A Notion onboarding email with starter workspace guidance and suggested first actions.",
    category: "onboarding",
    tags: ["notion", "workspace", "docs", "onboarding"],
    previewClass: "bg-neutral-200",
    createdAt: "2025-12-20",
  },
  {
    id: "notion-workspace-invite",
    slug: "workspace-invite",
    brand: "notion",
    title: "Workspace Invite",
    description: "A Notion invite email for joining a team workspace with page access and collaboration context.",
    category: "onboarding",
    tags: ["notion", "invite", "workspace", "team"],
    previewClass: "bg-stone-200",
    createdAt: "2025-12-20",
  },
  {
    id: "linear-welcome",
    slug: "welcome",
    brand: "linear",
    title: "Welcome to Linear",
    description: "A product-team onboarding email with cycle context, integrations, and workflow setup.",
    category: "onboarding",
    tags: ["linear", "welcome", "product", "issues"],
    previewClass: "bg-indigo-100",
    createdAt: "2025-12-22",
  },
  {
    id: "linear-issue-assigned",
    slug: "issue-assigned",
    brand: "linear",
    title: "Issue Assigned",
    description: "A Linear issue assignment notification with issue details, priority, cycle context, and project link.",
    category: "collaboration",
    tags: ["linear", "issue", "assignment", "engineering"],
    previewClass: "bg-violet-100",
    createdAt: "2026-03-28",
  },
  {
    id: "spotify-wrapped-summary",
    slug: "wrapped-summary",
    brand: "spotify",
    title: "Wrapped Summary",
    description: "A bold year-in-review email with listening stats, top artist, and playlist CTA.",
    category: "marketing",
    tags: ["spotify", "wrapped", "music", "year-in-review"],
    previewClass: "bg-emerald-100",
    createdAt: "2025-12-25",
  },
  {
    id: "spotify-new-release",
    slug: "new-release",
    brand: "spotify",
    title: "New Release",
    description: "A Spotify new release notification for a followed artist with album details and listening CTA.",
    category: "marketing",
    tags: ["spotify", "new-release", "music", "album"],
    previewClass: "bg-green-100",
    createdAt: "2026-03-28",
  },
  {
    id: "netflix-new-arrivals",
    slug: "new-arrivals",
    brand: "netflix",
    title: "New Arrivals",
    description: "A streaming recommendation email spotlighting fresh releases tailored to viewing history.",
    category: "product-update",
    tags: ["netflix", "streaming", "recommendations", "entertainment"],
    previewClass: "bg-red-100",
    createdAt: "2026-01-02",
  },
  {
    id: "netflix-account-update",
    slug: "account-update",
    brand: "netflix",
    title: "Account Update",
    description: "A Netflix plan change confirmation with new plan details, billing date, and feature comparison.",
    category: "transactional",
    tags: ["netflix", "account", "plan", "billing"],
    previewClass: "bg-rose-100",
    createdAt: "2026-03-28",
  },
  {
    id: "paypal-payment-sent",
    slug: "payment-sent",
    brand: "paypal",
    title: "Payment Sent",
    description: "A PayPal payment confirmation with transaction details, funding source, and buyer protection info.",
    category: "invoice",
    tags: ["paypal", "payment", "send-money", "p2p"],
    previewClass: "bg-blue-100",
    createdAt: "2026-01-05",
  },
  {
    id: "paypal-money-received",
    slug: "money-received",
    brand: "paypal",
    title: "Money Received",
    description: "A PayPal incoming payment notification with net amount, fee breakdown, and balance update.",
    category: "invoice",
    tags: ["paypal", "payment", "received", "balance"],
    previewClass: "bg-indigo-100",
    createdAt: "2026-01-05",
  },
  {
    id: "paypal-security-alert",
    slug: "security-alert",
    brand: "paypal",
    title: "Security Alert",
    description: "A PayPal account security alert with sign-in details, blocked status, and remediation steps.",
    category: "security",
    tags: ["paypal", "security", "signin", "account"],
    previewClass: "bg-blue-200",
    createdAt: "2026-01-08",
  },
  {
    id: "microsoft-teams-meeting-invite",
    slug: "teams-meeting-invite",
    brand: "microsoft",
    title: "Teams Meeting Invite",
    description: "A Microsoft Teams meeting invitation with agenda, attendees, and dial-in join options.",
    category: "collaboration",
    tags: ["microsoft", "teams", "meeting", "calendar"],
    previewClass: "bg-sky-100",
    createdAt: "2026-01-10",
  },
  {
    id: "microsoft-azure-billing",
    slug: "azure-billing",
    brand: "microsoft",
    title: "Azure Billing Statement",
    description: "An Azure monthly billing summary with service-level cost breakdown and trend analysis.",
    category: "invoice",
    tags: ["microsoft", "azure", "billing", "cloud", "invoice"],
    previewClass: "bg-cyan-100",
    createdAt: "2026-01-10",
  },
  {
    id: "atlassian-jira-issue-assigned",
    slug: "jira-issue-assigned",
    brand: "atlassian",
    title: "Jira Issue Assigned",
    description: "A Jira issue assignment notification with story details, sprint context, and linked epic.",
    category: "collaboration",
    tags: ["atlassian", "jira", "issue", "assignment", "agile"],
    previewClass: "bg-blue-100",
    createdAt: "2026-01-12",
  },
  {
    id: "atlassian-sprint-summary",
    slug: "sprint-summary",
    brand: "atlassian",
    title: "Sprint Summary",
    description: "A Jira sprint completion report with velocity metrics, completed issues, and carryover items.",
    category: "collaboration",
    tags: ["atlassian", "jira", "sprint", "agile", "report"],
    previewClass: "bg-indigo-100",
    createdAt: "2026-01-12",
  },
  {
    id: "zoom-meeting-invitation",
    slug: "meeting-invitation",
    brand: "zoom",
    title: "Meeting Invitation",
    description: "A Zoom meeting invite with join link, meeting ID, passcode, and dial-in details.",
    category: "collaboration",
    tags: ["zoom", "meeting", "video", "invitation"],
    previewClass: "bg-sky-100",
    createdAt: "2026-01-15",
  },
  {
    id: "zoom-webinar-confirmation",
    slug: "webinar-confirmation",
    brand: "zoom",
    title: "Webinar Confirmation",
    description: "A Zoom webinar registration confirmation with event details and session preview.",
    category: "confirmation",
    tags: ["zoom", "webinar", "registration", "event"],
    previewClass: "bg-blue-100",
    createdAt: "2026-01-15",
  },
  {
    id: "calendly-event-scheduled",
    slug: "event-scheduled",
    brand: "calendly",
    title: "Event Scheduled",
    description: "A Calendly booking confirmation with event type, invitee details, and custom responses.",
    category: "transactional",
    tags: ["calendly", "scheduling", "booking", "calendar"],
    previewClass: "bg-blue-100",
    createdAt: "2026-01-18",
  },
  {
    id: "calendly-event-reminder",
    slug: "event-reminder",
    brand: "calendly",
    title: "Event Reminder",
    description: "A Calendly pre-meeting reminder with event details and quick-join link.",
    category: "retention",
    tags: ["calendly", "reminder", "meeting", "calendar"],
    previewClass: "bg-indigo-100",
    createdAt: "2026-01-18",
  },
  {
    id: "hubspot-form-submission",
    slug: "form-submission",
    brand: "hubspot",
    title: "Form Submission",
    description: "A HubSpot form submission alert with lead details, message content, and CRM link.",
    category: "alerts",
    tags: ["hubspot", "form", "lead", "crm", "marketing"],
    previewClass: "bg-orange-100",
    createdAt: "2026-01-20",
  },
  {
    id: "hubspot-deal-closed",
    slug: "deal-closed",
    brand: "hubspot",
    title: "Deal Closed Won",
    description: "A HubSpot deal closure notification with revenue metrics, deal summary, and next steps.",
    category: "transactional",
    tags: ["hubspot", "deal", "sales", "crm", "revenue"],
    previewClass: "bg-amber-100",
    createdAt: "2026-01-20",
  },
  {
    id: "loom-video-shared",
    slug: "video-shared",
    brand: "loom",
    title: "Video Shared",
    description: "A Loom video sharing notification with recording details, sender message, and watch CTA.",
    category: "transactional",
    tags: ["loom", "video", "sharing", "async"],
    previewClass: "bg-violet-100",
    createdAt: "2026-01-22",
  },
  {
    id: "loom-comment-notification",
    slug: "comment-notification",
    brand: "loom",
    title: "Comment Notification",
    description: "A Loom comment alert with timestamped feedback, video stats, and reply link.",
    category: "alerts",
    tags: ["loom", "comment", "video", "feedback"],
    previewClass: "bg-purple-100",
    createdAt: "2026-01-22",
  },
  {
    id: "typeform-form-response",
    slug: "form-response",
    brand: "typeform",
    title: "Form Response",
    description: "A Typeform response notification with NPS score, answer summary, and open-ended feedback.",
    category: "alerts",
    tags: ["typeform", "form", "survey", "response", "feedback"],
    previewClass: "bg-zinc-200",
    createdAt: "2026-01-25",
  },
  {
    id: "typeform-survey-completed",
    slug: "survey-completed",
    brand: "typeform",
    title: "Survey Completed",
    description: "A Typeform survey completion summary with response count, completion rate, and top insights.",
    category: "transactional",
    tags: ["typeform", "survey", "completed", "analytics"],
    previewClass: "bg-stone-200",
    createdAt: "2026-03-28",
  },
  {
    id: "pinterest-pin-notification",
    slug: "pin-notification",
    brand: "pinterest",
    title: "Pin Notification",
    description: "A Pinterest pin activity email notifying a creator that their pin was saved to someone's board, with save count and impressions.",
    category: "alerts",
    tags: ["pinterest", "pin", "social", "notification"],
    previewClass: "bg-rose-100",
    createdAt: "2026-02-01",
  },
  {
    id: "pinterest-board-invite",
    slug: "board-invite",
    brand: "pinterest",
    title: "Board Invite",
    description: "A Pinterest collaborative board invitation email with board details, collaborator count, and a clear accept CTA.",
    category: "onboarding",
    tags: ["pinterest", "board", "invite", "collaboration"],
    previewClass: "bg-red-100",
    createdAt: "2026-02-01",
  },
  {
    id: "instagram-follower-notification",
    slug: "follower-notification",
    brand: "instagram",
    title: "New Follower",
    description: "An Instagram follower notification with profile stats, weekly account metrics, and a profile view CTA.",
    category: "alerts",
    tags: ["instagram", "follower", "social", "notification"],
    previewClass: "bg-fuchsia-100",
    createdAt: "2026-02-05",
  },
  {
    id: "instagram-story-mention",
    slug: "story-mention",
    brand: "instagram",
    title: "Story Mention",
    description: "An Instagram story mention notification with mention details, story expiry timer, and reshare guidance.",
    category: "social",
    tags: ["instagram", "story", "mention", "social"],
    previewClass: "bg-pink-100",
    createdAt: "2026-02-05",
  },
  {
    id: "youtube-new-video-notification",
    slug: "new-video-notification",
    brand: "youtube",
    title: "New Video Notification",
    description: "A YouTube new upload notification for a subscribed channel with video details, channel stats, and a watch now CTA.",
    category: "alerts",
    tags: ["youtube", "video", "subscription", "notification"],
    previewClass: "bg-red-100",
    createdAt: "2026-02-08",
  },
  {
    id: "youtube-channel-update",
    slug: "channel-update",
    brand: "youtube",
    title: "Channel Milestone",
    description: "A YouTube creator milestone email celebrating 10K subscribers with channel performance metrics and unlocked features.",
    category: "product-update",
    tags: ["youtube", "channel", "milestone", "creator"],
    previewClass: "bg-orange-100",
    createdAt: "2026-02-08",
  },
  {
    id: "tiktok-follower-notification",
    slug: "follower-notification",
    brand: "tiktok",
    title: "New Follower",
    description: "A TikTok follower notification with the new follower's profile, weekly account performance metrics, and growth tips.",
    category: "alerts",
    tags: ["tiktok", "follower", "social", "notification"],
    previewClass: "bg-zinc-200",
    createdAt: "2026-02-10",
  },
  {
    id: "tiktok-video-trending",
    slug: "video-trending",
    brand: "tiktok",
    title: "Video Trending",
    description: "A TikTok trending video alert with view count, engagement metrics, and tips for maximizing reach.",
    category: "social",
    tags: ["tiktok", "trending", "video", "viral"],
    previewClass: "bg-stone-200",
    createdAt: "2026-03-28",
  },
  {
    id: "linkedin-connection-request",
    slug: "connection-request",
    brand: "linkedin",
    title: "Connection Request",
    description: "A LinkedIn connection request email with sender details, mutual connections, a personal message, and shared experience highlights.",
    category: "social",
    tags: ["linkedin", "connection", "networking", "social"],
    previewClass: "bg-sky-100",
    createdAt: "2026-02-12",
  },
  {
    id: "linkedin-job-alert",
    slug: "job-alert",
    brand: "linkedin",
    title: "Job Alert",
    description: "A LinkedIn job alert email with top matching roles, salary ranges, and application tips tailored to saved preferences.",
    category: "alerts",
    tags: ["linkedin", "jobs", "career", "alert"],
    previewClass: "bg-blue-100",
    createdAt: "2026-02-12",
  },
  {
    id: "mailchimp-campaign-report",
    slug: "campaign-report",
    brand: "mailchimp",
    title: "Campaign Report",
    description: "A Mailchimp campaign performance report with open rates, click rates, top links, and industry benchmark comparisons.",
    category: "newsletter",
    tags: ["mailchimp", "campaign", "email-marketing", "report"],
    previewClass: "bg-yellow-100",
    createdAt: "2026-02-15",
  },
  {
    id: "mailchimp-audience-growth",
    slug: "audience-growth",
    brand: "mailchimp",
    title: "Audience Growth",
    description: "A Mailchimp monthly audience report with subscriber growth, signup sources, and list health metrics.",
    category: "marketing",
    tags: ["mailchimp", "audience", "growth", "subscribers"],
    previewClass: "bg-amber-100",
    createdAt: "2026-03-28",
  },
  {
    id: "twitch-stream-live",
    slug: "stream-live",
    brand: "twitch",
    title: "Stream Live Notification",
    description: "A Twitch live stream notification email for a followed channel with stream title, game category, viewer count, and a watch CTA.",
    category: "alerts",
    tags: ["twitch", "stream", "live", "notification", "gaming"],
    previewClass: "bg-violet-100",
    createdAt: "2026-02-18",
  },
  {
    id: "twitch-subscription-gift",
    slug: "subscription-gift",
    brand: "twitch",
    title: "Subscription Gift",
    description: "A Twitch gift subscription notification with gifter details, channel info, and subscriber perks.",
    category: "transactional",
    tags: ["twitch", "subscription", "gift", "channel"],
    previewClass: "bg-purple-100",
    createdAt: "2026-03-28",
  },
  {
    id: "reddit-trending-post",
    slug: "trending-post",
    brand: "reddit",
    title: "Trending Post",
    description: "A Reddit trending post digest email highlighting top posts in followed communities with upvotes, comments, and thread links.",
    category: "social",
    tags: ["reddit", "trending", "community", "digest"],
    previewClass: "bg-orange-100",
    createdAt: "2026-02-20",
  },
  {
    id: "reddit-comment-reply",
    slug: "comment-reply",
    brand: "reddit",
    title: "Comment Reply",
    description: "A Reddit comment reply notification with the original comment, reply content, upvote counts, and a direct thread link.",
    category: "social",
    tags: ["reddit", "comment", "reply", "notification"],
    previewClass: "bg-red-100",
    createdAt: "2026-02-20",
  },
  {
    id: "medium-new-follower",
    slug: "new-follower",
    brand: "medium",
    title: "New Follower",
    description: "A Medium new follower notification with follower profile, writer stats, and monthly read ratio breakdown.",
    category: "social",
    tags: ["medium", "follower", "writing", "notification"],
    previewClass: "bg-green-100",
    createdAt: "2026-02-22",
  },
  {
    id: "medium-publication-invite",
    slug: "publication-invite",
    brand: "medium",
    title: "Publication Invite",
    description: "A Medium publication contributor invitation with publication stats, audience reach, submission guidelines, and benefits.",
    category: "onboarding",
    tags: ["medium", "publication", "writing", "invite"],
    previewClass: "bg-emerald-100",
    createdAt: "2026-02-22",
  },
  {
    id: "coinbase-price-alert",
    slug: "price-alert",
    brand: "coinbase",
    title: "Price Alert",
    description: "A Coinbase price alert email triggered when Bitcoin crosses a user-defined target, with market snapshot and 24h range.",
    category: "alerts",
    tags: ["coinbase", "crypto", "bitcoin", "price-alert"],
    previewClass: "bg-blue-100",
    createdAt: "2026-02-25",
  },
  {
    id: "coinbase-transaction-confirmation",
    slug: "transaction-confirmation",
    brand: "coinbase",
    title: "Transaction Confirmation",
    description: "A Coinbase crypto purchase confirmation with full transaction breakdown, network confirmations, and fee details.",
    category: "confirmation",
    tags: ["coinbase", "crypto", "transaction", "receipt"],
    previewClass: "bg-indigo-100",
    createdAt: "2026-02-25",
  },
  {
    id: "intercom-new-message",
    slug: "new-message",
    brand: "intercom",
    title: "New Message",
    description: "An Intercom new conversation notification with visitor details, message preview, and inbox assignment.",
    category: "transactional",
    tags: ["intercom", "message", "support", "inbox"],
    previewClass: "bg-blue-100",
    createdAt: "2026-03-01",
  },
  {
    id: "intercom-conversation-closed",
    slug: "conversation-closed",
    brand: "intercom",
    title: "Conversation Closed",
    description: "An Intercom conversation resolution summary with response metrics, resolution details, and CSAT prompt.",
    category: "transactional",
    tags: ["intercom", "conversation", "support", "resolved"],
    previewClass: "bg-indigo-100",
    createdAt: "2026-03-01",
  },
  {
    id: "sendgrid-email-delivered",
    slug: "email-delivered",
    brand: "sendgrid",
    title: "Email Delivered",
    description: "A SendGrid campaign delivery report with open rates, click rates, and delivery metrics.",
    category: "confirmation",
    tags: ["sendgrid", "email", "delivery", "campaign"],
    previewClass: "bg-sky-100",
    createdAt: "2026-03-05",
  },
  {
    id: "sendgrid-bounce-alert",
    slug: "bounce-alert",
    brand: "sendgrid",
    title: "Bounce Alert",
    description: "A SendGrid bounce rate alert with threshold details, bounce breakdown, and remediation steps.",
    category: "alerts",
    tags: ["sendgrid", "bounce", "alert", "deliverability"],
    previewClass: "bg-orange-100",
    createdAt: "2026-03-05",
  },
  {
    id: "twilio-sms-delivered",
    slug: "sms-delivered",
    brand: "twilio",
    title: "SMS Delivered",
    description: "A Twilio SMS campaign delivery report with delivery rate, segment usage, and failure summary.",
    category: "transactional",
    tags: ["twilio", "sms", "delivery", "messaging"],
    previewClass: "bg-red-100",
    createdAt: "2026-03-08",
  },
  {
    id: "twilio-account-alert",
    slug: "account-alert",
    brand: "twilio",
    title: "Account Alert",
    description: "A Twilio account usage spike alert with overage details, threshold info, and recommended actions.",
    category: "alerts",
    tags: ["twilio", "account", "alert", "usage"],
    previewClass: "bg-rose-100",
    createdAt: "2026-03-08",
  },
  {
    id: "docusign-document-signed",
    slug: "document-signed",
    brand: "docusign",
    title: "Document Signed",
    description: "A DocuSign completion notification confirming all parties have signed, with signing activity timeline and download link.",
    category: "confirmation",
    tags: ["docusign", "signature", "document", "completed"],
    previewClass: "bg-violet-100",
    createdAt: "2026-03-12",
  },
  {
    id: "docusign-signature-requested",
    slug: "signature-requested",
    brand: "docusign",
    title: "Signature Requested",
    description: "A DocuSign signature request with document details, expiration date, and signing guidance.",
    category: "transactional",
    tags: ["docusign", "signature", "request", "sign"],
    previewClass: "bg-purple-100",
    createdAt: "2026-03-12",
  },
  {
    id: "clickup-task-assigned",
    slug: "task-assigned",
    brand: "clickup",
    title: "Task Assigned",
    description: "A ClickUp task assignment notification with task details, subtasks, priority, and due date.",
    category: "collaboration",
    tags: ["clickup", "task", "assignment", "project-management"],
    previewClass: "bg-indigo-100",
    createdAt: "2026-03-15",
  },
  {
    id: "clickup-sprint-update",
    slug: "sprint-update",
    brand: "clickup",
    title: "Sprint Update",
    description: "A ClickUp sprint progress report with completed tasks, remaining work, and team velocity metrics.",
    category: "transactional",
    tags: ["clickup", "sprint", "progress", "project-management"],
    previewClass: "bg-violet-100",
    createdAt: "2026-03-28",
  },
  {
    id: "salesforce-opportunity-won",
    slug: "opportunity-won",
    brand: "salesforce",
    title: "Opportunity Won",
    description: "A Salesforce closed-won alert with deal metrics, account details, and competitive context.",
    category: "transactional",
    tags: ["salesforce", "opportunity", "sales", "crm", "deal"],
    previewClass: "bg-cyan-100",
    createdAt: "2026-03-18",
  },
  {
    id: "salesforce-lead-assigned",
    slug: "lead-assigned",
    brand: "salesforce",
    title: "Lead Assigned",
    description: "A Salesforce new lead assignment notification with lead score, contact details, and recommended next actions.",
    category: "collaboration",
    tags: ["salesforce", "lead", "assignment", "crm", "sales"],
    previewClass: "bg-blue-100",
    createdAt: "2026-03-28",
  },
  {
    id: "nike-order-confirmation",
    slug: "order-confirmation",
    brand: "nike",
    title: "Order Confirmation",
    description: "A Nike order confirmation with product details, delivery estimate, and order management options.",
    category: "confirmation",
    tags: ["nike", "order", "ecommerce", "receipt"],
    previewClass: "bg-zinc-200",
    createdAt: "2026-03-20",
  },
  {
    id: "doordash-delivery-update",
    slug: "delivery-update",
    brand: "doordash",
    title: "Delivery Update",
    description: "A DoorDash delivery status update with order details, Dasher info, and live tracking link.",
    category: "transactional",
    tags: ["doordash", "delivery", "food", "tracking"],
    previewClass: "bg-red-100",
    createdAt: "2026-03-20",
  },
  {
    id: "tesla-service-appointment",
    slug: "service-appointment",
    brand: "tesla",
    title: "Service Appointment",
    description: "A Tesla service appointment confirmation with vehicle details, service center info, and preparation checklist.",
    category: "transactional",
    tags: ["tesla", "service", "appointment", "automotive"],
    previewClass: "bg-red-100",
    createdAt: "2026-03-20",
  },
  // ── Revolut ──────────────────────────────────────────────
  {
    id: "revolut-transaction-alert",
    slug: "transaction-alert",
    brand: "revolut",
    title: "Transaction Alert",
    description: "A Revolut real-time transaction alert with merchant details, amount, and account balance.",
    category: "alerts",
    tags: ["revolut", "transaction", "fintech", "banking"],
    previewClass: "bg-blue-100",
    createdAt: "2026-04-05",
  },
  {
    id: "revolut-card-delivered",
    slug: "card-delivered",
    brand: "revolut",
    title: "Card Delivered",
    description: "A Revolut Metal card delivery confirmation with activation steps and Apple/Google Pay setup guidance.",
    category: "onboarding",
    tags: ["revolut", "card", "fintech", "banking"],
    previewClass: "bg-indigo-100",
    createdAt: "2026-04-05",
  },
  // ── Monzo ────────────────────────────────────────────────
  {
    id: "monzo-payment-received",
    slug: "payment-received",
    brand: "monzo",
    title: "Payment Received",
    description: "A Monzo instant payment received notification with sender details and updated balance.",
    category: "alerts",
    tags: ["monzo", "payment", "fintech", "banking"],
    previewClass: "bg-rose-100",
    createdAt: "2026-04-05",
  },
  {
    id: "monzo-monthly-summary",
    slug: "monthly-summary",
    brand: "monzo",
    title: "Monthly Summary",
    description: "A Monzo monthly spending summary with category breakdown and pot balances.",
    category: "newsletter",
    tags: ["monzo", "spending", "summary", "fintech"],
    previewClass: "bg-pink-100",
    createdAt: "2026-04-05",
  },
  // ── Asana ────────────────────────────────────────────────
  {
    id: "asana-task-assigned",
    slug: "task-assigned",
    brand: "asana",
    title: "Task Assigned",
    description: "An Asana task assignment notification with task details, description, project, and due date.",
    category: "collaboration",
    tags: ["asana", "task", "project-management", "productivity"],
    previewClass: "bg-red-100",
    createdAt: "2026-04-05",
  },
  {
    id: "asana-project-update",
    slug: "project-update",
    brand: "asana",
    title: "Project Status Update",
    description: "An Asana weekly project health update with status, milestone progress, and notes.",
    category: "collaboration",
    tags: ["asana", "project", "status", "team"],
    previewClass: "bg-orange-100",
    createdAt: "2026-04-05",
  },
  // ── monday.com ───────────────────────────────────────────
  {
    id: "monday-item-assigned",
    slug: "item-assigned",
    brand: "monday",
    title: "Item Assigned",
    description: "A monday.com item assignment notification with board details, due date, and status.",
    category: "collaboration",
    tags: ["monday", "item", "project-management", "productivity"],
    previewClass: "bg-orange-100",
    createdAt: "2026-04-05",
  },
  {
    id: "monday-deadline-reminder",
    slug: "deadline-reminder",
    brand: "monday",
    title: "Deadline Reminder",
    description: "A monday.com deadline reminder for items due the next day with guidance on status updates.",
    category: "alerts",
    tags: ["monday", "deadline", "reminder", "project-management"],
    previewClass: "bg-red-100",
    createdAt: "2026-04-05",
  },
  // ── Supabase ─────────────────────────────────────────────
  {
    id: "supabase-welcome",
    slug: "welcome",
    brand: "supabase",
    title: "Welcome Email",
    description: "A Supabase project creation confirmation with database details and quick-start guide.",
    category: "onboarding",
    tags: ["supabase", "postgres", "developer", "database"],
    previewClass: "bg-emerald-100",
    createdAt: "2026-04-05",
  },
  {
    id: "supabase-usage-alert",
    slug: "usage-alert",
    brand: "supabase",
    title: "Usage Alert",
    description: "A Supabase plan limit warning with current usage metrics and upgrade recommendation.",
    category: "alerts",
    tags: ["supabase", "usage", "billing", "database"],
    previewClass: "bg-green-100",
    createdAt: "2026-04-05",
  },
  // ── Netlify ──────────────────────────────────────────────
  {
    id: "netlify-deployment-success",
    slug: "deployment-success",
    brand: "netlify",
    title: "Deployment Success",
    description: "A Netlify deployment success notification with deploy details, commit info, and live site link.",
    category: "alerts",
    tags: ["netlify", "deploy", "developer", "ci-cd"],
    previewClass: "bg-teal-100",
    createdAt: "2026-04-05",
  },
  {
    id: "netlify-form-submission",
    slug: "form-submission",
    brand: "netlify",
    title: "Form Submission",
    description: "A Netlify form submission notification with full field data from a contact form.",
    category: "alerts",
    tags: ["netlify", "form", "submission", "contact"],
    previewClass: "bg-cyan-100",
    createdAt: "2026-04-05",
  },
  // ── Resend ───────────────────────────────────────────────
  {
    id: "resend-email-delivered",
    slug: "email-delivered",
    brand: "resend",
    title: "Email Delivered",
    description: "A Resend email delivery notification with message details and event timeline.",
    category: "alerts",
    tags: ["resend", "email", "delivery", "developer"],
    previewClass: "bg-slate-100",
    createdAt: "2026-04-05",
  },
  {
    id: "resend-api-key-created",
    slug: "api-key-created",
    brand: "resend",
    title: "API Key Created",
    description: "A Resend security alert for new API key creation with key details and fraud guidance.",
    category: "security",
    tags: ["resend", "api-key", "security", "developer"],
    previewClass: "bg-zinc-100",
    createdAt: "2026-04-05",
  },
  // ── Neon ─────────────────────────────────────────────────
  {
    id: "neon-database-created",
    slug: "database-created",
    brand: "neon",
    title: "Database Created",
    description: "A Neon serverless Postgres project creation email with connection details and quick-start guide.",
    category: "onboarding",
    tags: ["neon", "postgres", "database", "developer"],
    previewClass: "bg-emerald-100",
    createdAt: "2026-04-05",
  },
  // ── Sentry ───────────────────────────────────────────────
  {
    id: "sentry-new-issue",
    slug: "new-issue",
    brand: "sentry",
    title: "New Issue Detected",
    description: "A Sentry new error alert with stack trace, occurrence count, and affected users.",
    category: "alerts",
    tags: ["sentry", "error", "monitoring", "developer"],
    previewClass: "bg-purple-100",
    createdAt: "2026-04-05",
  },
  {
    id: "sentry-weekly-digest",
    slug: "weekly-digest",
    brand: "sentry",
    title: "Weekly Digest",
    description: "A Sentry weekly error and performance summary with crash-free sessions and top issues.",
    category: "newsletter",
    tags: ["sentry", "digest", "errors", "monitoring"],
    previewClass: "bg-indigo-100",
    createdAt: "2026-04-05",
  },
  // ── PostHog ──────────────────────────────────────────────
  {
    id: "posthog-weekly-report",
    slug: "weekly-report",
    brand: "posthog",
    title: "Weekly Analytics Report",
    description: "A PostHog product analytics weekly report with user metrics, top events, and conversion rates.",
    category: "newsletter",
    tags: ["posthog", "analytics", "metrics", "product"],
    previewClass: "bg-orange-100",
    createdAt: "2026-04-05",
  },
  // ── Etsy ─────────────────────────────────────────────────
  {
    id: "etsy-order-confirmation",
    slug: "order-confirmation",
    brand: "etsy",
    title: "Order Confirmation",
    description: "An Etsy order confirmation for a handmade item with seller, shipping, and delivery details.",
    category: "confirmation",
    tags: ["etsy", "order", "ecommerce", "marketplace"],
    previewClass: "bg-orange-100",
    createdAt: "2026-04-05",
  },
  {
    id: "etsy-review-request",
    slug: "review-request",
    brand: "etsy",
    title: "Review Request",
    description: "An Etsy post-delivery review request encouraging buyers to support independent makers.",
    category: "follow-up",
    tags: ["etsy", "review", "feedback", "ecommerce"],
    previewClass: "bg-amber-100",
    createdAt: "2026-04-05",
  },
  // ── eBay ─────────────────────────────────────────────────
  {
    id: "ebay-order-confirmation",
    slug: "order-confirmation",
    brand: "ebay",
    title: "Order Confirmation",
    description: "An eBay order confirmation for a used item purchase with seller rating and Money Back Guarantee details.",
    category: "confirmation",
    tags: ["ebay", "order", "ecommerce", "marketplace"],
    previewClass: "bg-blue-100",
    createdAt: "2026-04-05",
  },
  {
    id: "ebay-offer-received",
    slug: "offer-received",
    brand: "ebay",
    title: "Offer Received",
    description: "An eBay Best Offer notification for sellers with offer details and accept/counter/decline options.",
    category: "alerts",
    tags: ["ebay", "offer", "selling", "marketplace"],
    previewClass: "bg-sky-100",
    createdAt: "2026-04-05",
  },
  // ── Eventbrite ───────────────────────────────────────────
  {
    id: "eventbrite-ticket-confirmation",
    slug: "ticket-confirmation",
    brand: "eventbrite",
    title: "Ticket Confirmation",
    description: "An Eventbrite ticket booking confirmation with event details, ticket type, and venue information.",
    category: "confirmation",
    tags: ["eventbrite", "ticket", "event", "booking"],
    previewClass: "bg-orange-100",
    createdAt: "2026-04-05",
  },
  {
    id: "eventbrite-event-reminder",
    slug: "event-reminder",
    brand: "eventbrite",
    title: "Event Reminder",
    description: "An Eventbrite day-before event reminder with venue directions and what to bring.",
    category: "transactional",
    tags: ["eventbrite", "reminder", "event", "ticket"],
    previewClass: "bg-red-100",
    createdAt: "2026-04-05",
  },
  // ── Coursera ─────────────────────────────────────────────
  {
    id: "coursera-course-enrolled",
    slug: "course-enrolled",
    brand: "coursera",
    title: "Course Enrollment Confirmation",
    description: "A Coursera enrollment confirmation with course details, instructor info, and learning outcomes.",
    category: "onboarding",
    tags: ["coursera", "course", "learning", "education"],
    previewClass: "bg-blue-100",
    createdAt: "2026-04-05",
  },
  {
    id: "coursera-certificate-earned",
    slug: "certificate-earned",
    brand: "coursera",
    title: "Certificate Earned",
    description: "A Coursera course completion certificate notification with verification link and next course suggestion.",
    category: "transactional",
    tags: ["coursera", "certificate", "achievement", "education"],
    previewClass: "bg-indigo-100",
    createdAt: "2026-04-05",
  },
  // ── OpenAI ───────────────────────────────────────────────
  {
    id: "openai-api-key-created",
    slug: "api-key-created",
    brand: "openai",
    title: "API Key Created",
    description: "An OpenAI security notification for new API key creation with key details and security guidance.",
    category: "security",
    tags: ["openai", "api-key", "security", "developer"],
    previewClass: "bg-emerald-100",
    createdAt: "2026-04-05",
  },
  {
    id: "openai-usage-limit-warning",
    slug: "usage-limit-warning",
    brand: "openai",
    title: "Usage Limit Warning",
    description: "An OpenAI API spending limit alert with model cost breakdown and optimization tips.",
    category: "alerts",
    tags: ["openai", "usage", "billing", "api"],
    previewClass: "bg-teal-100",
    createdAt: "2026-04-05",
  },
  // Adobe
  {
    id: "adobe-creative-cloud-update",
    slug: "creative-cloud-update",
    brand: "adobe",
    title: "Creative Cloud App Updates",
    description: "An Adobe Creative Cloud notification for available app updates across Photoshop, Illustrator, and Premiere Pro.",
    category: "product-update",
    tags: ["adobe", "creative-cloud", "app-update", "photoshop"],
    previewClass: "bg-red-100",
    createdAt: "2026-04-05",
  },
  {
    id: "adobe-subscription-renewal",
    slug: "subscription-renewal",
    brand: "adobe",
    title: "Subscription Renewal Notice",
    description: "An Adobe Creative Cloud billing reminder with upcoming renewal details and plan summary.",
    category: "transactional",
    tags: ["adobe", "subscription", "billing", "renewal"],
    previewClass: "bg-rose-100",
    createdAt: "2026-04-05",
  },
  // Booking.com
  {
    id: "booking-booking-confirmation",
    slug: "booking-confirmation",
    brand: "booking",
    title: "Booking Confirmation",
    description: "A Booking.com reservation confirmation with hotel details, dates, and check-in instructions.",
    category: "confirmation",
    tags: ["booking", "hotel", "travel", "confirmation"],
    previewClass: "bg-blue-100",
    createdAt: "2026-04-05",
  },
  {
    id: "booking-check-in-reminder",
    slug: "check-in-reminder",
    brand: "booking",
    title: "Check-In Reminder",
    description: "A Booking.com pre-arrival reminder with hotel address, check-in time, and travel tips.",
    category: "follow-up",
    tags: ["booking", "hotel", "check-in", "travel", "reminder"],
    previewClass: "bg-indigo-100",
    createdAt: "2026-04-05",
  },
  // Canva
  {
    id: "canva-design-shared",
    slug: "design-shared",
    brand: "canva",
    title: "Design Shared With You",
    description: "A Canva collaboration notification when a teammate shares a design with editor access.",
    category: "collaboration",
    tags: ["canva", "design", "collaboration", "shared"],
    previewClass: "bg-cyan-100",
    createdAt: "2026-04-05",
  },
  {
    id: "canva-team-invite",
    slug: "team-invite",
    brand: "canva",
    title: "Team Invitation",
    description: "A Canva team invitation email to join a shared workspace with brand kit access.",
    category: "onboarding",
    tags: ["canva", "team", "invite", "collaboration"],
    previewClass: "bg-sky-100",
    createdAt: "2026-04-05",
  },
  // DHL
  {
    id: "dhl-delivery-update",
    slug: "delivery-update",
    brand: "dhl",
    title: "Out for Delivery",
    description: "A DHL Express out-for-delivery notification with estimated delivery window and tracking link.",
    category: "transactional",
    tags: ["dhl", "delivery", "shipping", "tracking"],
    previewClass: "bg-yellow-100",
    createdAt: "2026-04-05",
  },
  {
    id: "dhl-shipping-confirmation",
    slug: "shipping-confirmation",
    brand: "dhl",
    title: "Shipping Confirmation",
    description: "A DHL Express shipping confirmation with tracking number, shipment details, and delivery address.",
    category: "transactional",
    tags: ["dhl", "shipping", "confirmation", "tracking"],
    previewClass: "bg-amber-100",
    createdAt: "2026-04-05",
  },
  // FedEx
  {
    id: "fedex-delivery-exception",
    slug: "delivery-exception",
    brand: "fedex",
    title: "Delivery Exception",
    description: "A FedEx delivery exception notification with failed delivery reason and redelivery options.",
    category: "alerts",
    tags: ["fedex", "delivery", "exception", "shipping"],
    previewClass: "bg-orange-100",
    createdAt: "2026-04-05",
  },
  {
    id: "fedex-shipment-tracking",
    slug: "shipment-tracking",
    brand: "fedex",
    title: "Shipment In Transit",
    description: "A FedEx shipment tracking update with in-transit status, estimated delivery, and tracking details.",
    category: "transactional",
    tags: ["fedex", "shipment", "tracking", "delivery"],
    previewClass: "bg-red-50",
    createdAt: "2026-04-05",
  },
  // Grammarly
  {
    id: "grammarly-premium-upgrade",
    slug: "premium-upgrade",
    brand: "grammarly",
    title: "Welcome to Grammarly Premium",
    description: "A Grammarly Premium upgrade confirmation welcoming users to advanced writing features.",
    category: "onboarding",
    tags: ["grammarly", "premium", "upgrade", "writing"],
    previewClass: "bg-green-100",
    createdAt: "2026-04-05",
  },
  {
    id: "grammarly-weekly-writing-stats",
    slug: "weekly-writing-stats",
    brand: "grammarly",
    title: "Weekly Writing Insights",
    description: "A Grammarly weekly stats digest with correctness score, words checked, and performance percentile.",
    category: "newsletter",
    tags: ["grammarly", "weekly", "stats", "writing", "digest"],
    previewClass: "bg-emerald-100",
    createdAt: "2026-04-05",
  },
  // H&M
  {
    id: "hm-loyalty-reward",
    slug: "loyalty-reward",
    brand: "hm",
    title: "Loyalty Reward Earned",
    description: "An H&M membership reward notification with voucher details and Gold status achievement.",
    category: "marketing",
    tags: ["hm", "loyalty", "reward", "voucher", "membership"],
    previewClass: "bg-red-100",
    createdAt: "2026-04-05",
  },
  {
    id: "hm-order-shipped",
    slug: "order-shipped",
    brand: "hm",
    title: "Order Shipped",
    description: "An H&M order shipping notification with tracking number and estimated delivery date.",
    category: "transactional",
    tags: ["hm", "order", "shipped", "tracking", "fashion"],
    previewClass: "bg-rose-50",
    createdAt: "2026-04-05",
  },
  // Klarna
  {
    id: "klarna-order-confirmation",
    slug: "order-confirmation",
    brand: "klarna",
    title: "Order Confirmed — Pay in 3",
    description: "A Klarna Pay in 3 order confirmation with payment schedule and merchant details.",
    category: "confirmation",
    tags: ["klarna", "bnpl", "order", "payment", "pay-in-3"],
    previewClass: "bg-pink-100",
    createdAt: "2026-04-05",
  },
  {
    id: "klarna-payment-reminder",
    slug: "payment-reminder",
    brand: "klarna",
    title: "Upcoming Payment Reminder",
    description: "A Klarna instalment payment reminder with due date, amount, and payment management link.",
    category: "follow-up",
    tags: ["klarna", "payment", "reminder", "bnpl", "instalment"],
    previewClass: "bg-fuchsia-100",
    createdAt: "2026-04-05",
  },
  // Microsoft Teams
  {
    id: "msteams-channel-mention",
    slug: "channel-mention",
    brand: "msteams",
    title: "Channel Mention",
    description: "A Microsoft Teams notification when a teammate mentions you in a channel conversation.",
    category: "alerts",
    tags: ["teams", "microsoft", "mention", "collaboration", "notification"],
    previewClass: "bg-blue-100",
    createdAt: "2026-04-05",
  },
  {
    id: "msteams-meeting-recording",
    slug: "meeting-recording",
    brand: "msteams",
    title: "Meeting Recording Ready",
    description: "A Microsoft Teams notification that a recorded meeting is available for playback in SharePoint.",
    category: "alerts",
    tags: ["teams", "microsoft", "meeting", "recording", "sharepoint"],
    previewClass: "bg-indigo-100",
    createdAt: "2026-04-05",
  },
  // Todoist
  {
    id: "todoist-daily-digest",
    slug: "daily-digest",
    brand: "todoist",
    title: "Daily Task Digest",
    description: "A Todoist daily digest listing tasks due today with priorities and a quick-action link.",
    category: "newsletter",
    tags: ["todoist", "tasks", "digest", "productivity", "daily"],
    previewClass: "bg-red-100",
    createdAt: "2026-04-05",
  },
  {
    id: "todoist-task-assigned",
    slug: "task-assigned",
    brand: "todoist",
    title: "Task Assigned to You",
    description: "A Todoist collaboration notification when a teammate assigns a task to you in a shared project.",
    category: "collaboration",
    tags: ["todoist", "task", "assigned", "collaboration", "productivity"],
    previewClass: "bg-orange-100",
    createdAt: "2026-04-05",
  },
  // UPS
  {
    id: "ups-delivery-notification",
    slug: "delivery-notification",
    brand: "ups",
    title: "Package Delivered",
    description: "A UPS delivery confirmation with delivery timestamp, signature, and package details.",
    category: "transactional",
    tags: ["ups", "delivery", "package", "confirmation", "tracking"],
    previewClass: "bg-amber-100",
    createdAt: "2026-04-05",
  },
  {
    id: "ups-shipping-confirmation",
    slug: "shipping-confirmation",
    brand: "ups",
    title: "Shipment Created",
    description: "A UPS shipping label creation confirmation with tracking number and expected delivery window.",
    category: "transactional",
    tags: ["ups", "shipping", "confirmation", "tracking", "label"],
    previewClass: "bg-yellow-100",
    createdAt: "2026-04-05",
  },
  // Wise
  {
    id: "wise-low-balance-alert",
    slug: "low-balance-alert",
    brand: "wise",
    title: "Low Balance Alert",
    description: "A Wise balance alert notification when a GBP account drops below a user-set threshold.",
    category: "alerts",
    tags: ["wise", "balance", "alert", "fintech", "gbp"],
    previewClass: "bg-green-100",
    createdAt: "2026-04-05",
  },
  {
    id: "wise-transfer-confirmation",
    slug: "transfer-confirmation",
    brand: "wise",
    title: "Transfer Confirmation",
    description: "A Wise international transfer confirmation with exchange rate, fees, and estimated arrival time.",
    category: "transactional",
    tags: ["wise", "transfer", "fintech", "fx", "confirmation"],
    previewClass: "bg-teal-100",
    createdAt: "2026-04-05",
  },
  // Zara
  {
    id: "zara-order-confirmation",
    slug: "order-confirmation",
    brand: "zara",
    title: "Order Confirmation",
    description: "A Zara order confirmation email with minimalist brand aesthetic and order summary.",
    category: "confirmation",
    tags: ["zara", "order", "fashion", "ecommerce", "confirmation"],
    previewClass: "bg-gray-100",
    createdAt: "2026-04-05",
  },
  {
    id: "zara-shipping-update",
    slug: "shipping-update",
    brand: "zara",
    title: "Order Dispatched",
    description: "A Zara shipping update with dispatch confirmation, estimated arrival, and tracking link.",
    category: "transactional",
    tags: ["zara", "shipping", "fashion", "ecommerce", "tracking"],
    previewClass: "bg-slate-100",
    createdAt: "2026-04-05",
  },
];

export function getTemplateByRoute(brand?: string, slug?: string) {
  if (!brand || !slug) {
    return undefined;
  }

  return templateLibrary.find((template) => template.brand === brand && template.slug === slug);
}
